import type { TermStatement } from "../../../api/content/ontologyApi";
import { isAbsoluteIri, isLanguageTag, suggestedTermIri, type TermEdit } from "./termPatches";

/** What the user asked to change; the proposal dialog collects the rest. */
export type TermEditRequest =
  | { readonly kind: "addLabel" }
  | { readonly kind: "renameLabel"; readonly statement: TermStatement }
  | { readonly kind: "editDefinition"; readonly statement: TermStatement | null }
  | { readonly kind: "addSubclass" };

/** The dialog's fields as typed. */
export interface TermEditDraft {
  readonly text: string;
  readonly language: string;
  /** `null` while the new class's IRI follows its label. */
  readonly iri: string | null;
}

export const DRAFT_PROBLEMS = ["empty", "unchanged", "language", "iri", "iriTaken"] as const;
export type DraftProblem = (typeof DRAFT_PROBLEMS)[number];

export interface ReadDraft {
  readonly edit: TermEdit;
  /** The first reason the draft cannot be proposed, or `null`. */
  readonly problem: DraftProblem | null;
  readonly subclassIri: string;
}

/** The statement a request replaces, if any. */
export function replacedStatement(request: TermEditRequest): TermStatement | null {
  return request.kind === "renameLabel" || request.kind === "editDefinition" ? request.statement : null;
}

/** The edit a draft stands for, and whether it can be proposed. */
export function readDraft(request: TermEditRequest, draft: TermEditDraft, termIri: string, taken: ReadonlySet<string>): ReadDraft {
  const text = draft.text.trim();
  const language = draft.language.trim() === "" ? null : draft.language.trim();
  const subclassIri = draft.iri ?? suggestedTermIri(termIri, text, taken);
  return { edit: editOf(request, text, language, subclassIri), problem: problemOf(request, text, language, subclassIri, taken), subclassIri };
}

function editOf(request: TermEditRequest, text: string, language: string | null, subclassIri: string): TermEdit {
  switch (request.kind) {
    case "addLabel":
      return { kind: "addLabel", text, language };
    case "renameLabel":
      return { kind: "renameLabel", statement: request.statement, text };
    case "editDefinition":
      return { kind: "editDefinition", statement: request.statement, text, language };
    case "addSubclass":
      return { kind: "addSubclass", iri: subclassIri, label: text, language };
  }
}

function problemOf(
  request: TermEditRequest,
  text: string,
  language: string | null,
  subclassIri: string,
  taken: ReadonlySet<string>,
): DraftProblem | null {
  if (text === "") {
    return "empty";
  }
  if (replacedStatement(request)?.text === text) {
    return "unchanged";
  }
  if (language !== null && !isLanguageTag(language)) {
    return "language";
  }
  if (request.kind === "addSubclass") {
    if (!isAbsoluteIri(subclassIri)) {
      return "iri";
    }
    if (taken.has(subclassIri)) {
      return "iriTaken";
    }
  }
  return null;
}
